import QrCreator from 'qr-creator';
import { t } from './i18n.js';

// --- localStorage helpers ---------------------------------------------------
// Every access is wrapped so a throwing/absent Storage (Safari private mode,
// disabled cookies) degrades to a no-op instead of crashing a caller. All the
// app's persistence goes through these so the try/catch lives in exactly one
// place.
export function getStored(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

export function setStored(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
}

export function removeStored(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* storage unavailable — nothing to remove */
  }
}

// --- View transitions -------------------------------------------------------
// Wrap a DOM mutation that swaps between the app's "screens" (role picker, QR
// connect, scanner, live view) so browsers with the View Transitions API
// cross-fade the old and new states and morph the shared, named elements — the
// logo and the QR card (see the view-transition-name rules in styles.css) — for
// a native, app-like feel.
//
// The snapshot the API captures is a cheap GPU-composited image and we only
// animate transform/opacity, so this stays smooth even mid-connection on modest
// phones. Where the API is missing, the user prefers reduced motion, or a
// transition is already running, the mutation just runs synchronously with the
// exact same end state and no animation.
export function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function withViewTransition(mutate) {
  if (prefersReducedMotion() || typeof document.startViewTransition !== 'function') {
    mutate();
    return;
  }
  try {
    document.startViewTransition(() => mutate());
  } catch (e) {
    // A failure to start a transition must never swallow the state change.
    mutate();
  }
}

export function getStoredJSON(key, fallback = null) {
  const raw = getStored(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

export function setStoredJSON(key, value) {
  return setStored(key, JSON.stringify(value));
}

// --- Parent identity + trusted-parents list ---------------------------------
// A baby monitor can remember which parent devices it has seen before so a
// recognized parent connects silently while a new one raises a heads-up. The
// parent keeps a stable random id (never a PeerJS id, which is per-load) so the
// same phone is recognized across reloads; the monitor keeps the trusted list.
// This is a recognition aid, NOT access control: the id is client-supplied, so
// it can be spoofed or omitted. Everything lives in localStorage only.
const CLIENT_ID_KEY = 'cammirror:client-id';
const CLIENT_NAME_KEY = 'cammirror:client-name';
const TRUSTED_PARENTS_KEY = 'cammirror:trusted-parents';

function randomId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (e) {
    /* fall through to the Math.random fallback below */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Return this parent device's stable `{ id, name }`, minting and persisting it
// on first use. The default name is just a short, non-identifying label the
// monitor can show in its trusted list; the id is what recognition keys on.
export function getClientIdentity() {
  let id = getStored(CLIENT_ID_KEY);
  if (!id) {
    id = randomId();
    setStored(CLIENT_ID_KEY, id);
  }
  let name = getStored(CLIENT_NAME_KEY);
  if (!name) {
    const suffix = id.replace(/[^a-z0-9]/gi, '').slice(-4).toUpperCase() || 'XXXX';
    name = t('trust.defaultName', { suffix });
    setStored(CLIENT_NAME_KEY, name);
  }
  return { id, name };
}

export function getTrustedParents() {
  const list = getStoredJSON(TRUSTED_PARENTS_KEY, []);
  return Array.isArray(list) ? list : [];
}

export function isTrustedParent(id) {
  if (!id) return false;
  return getTrustedParents().some((p) => p && p.id === id);
}

// Add (or refresh the name of) a trusted parent. Idempotent on id.
export function addTrustedParent(id, name) {
  if (!id) return false;
  const list = getTrustedParents().filter((p) => p && p.id !== id);
  list.push({ id, name: name || id, addedAt: Date.now() });
  return setStoredJSON(TRUSTED_PARENTS_KEY, list);
}

export function removeTrustedParent(id) {
  if (!id) return false;
  const list = getTrustedParents().filter((p) => p && p.id !== id);
  return setStoredJSON(TRUSTED_PARENTS_KEY, list);
}

// Rename a trusted parent so the monitor's list is meaningful (the parent-minted
// default is a generic label). No-op if the id isn't trusted or the name blank.
export function renameTrustedParent(id, name) {
  const clean = (name || '').trim();
  if (!id || !clean) return false;
  const list = getTrustedParents().map((p) => (p && p.id === id ? { ...p, name: clean } : p));
  return setStoredJSON(TRUSTED_PARENTS_KEY, list);
}

// --- Last-watched monitor (parent resume) -----------------------------------
// A parent remembers the last monitor it actually watched so a fully closed app
// can rejoin on reopen (the "Resume watching" shortcut) without walking back to
// the room to re-scan the QR. Kept for a day only: a monitor that's "still on /
// in the room" is a same-session/overnight thing, so we don't dial a long-dead
// id much later. The key + record shape live here so the writer (client.js) and
// the reader (app.js) can never drift apart.
const LAST_HOST_KEY = 'cammirror:last-host';
const LAST_HOST_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function rememberLastHost(id) {
  if (!id) return;
  setStoredJSON(LAST_HOST_KEY, { id, ts: Date.now() });
}

// The remembered host id if it's still fresh enough to offer a resume, else null.
export function loadResumableHostId() {
  const rec = getStoredJSON(LAST_HOST_KEY, null);
  if (!rec || !rec.id) return null;
  if (typeof rec.ts === 'number' && Date.now() - rec.ts > LAST_HOST_MAX_AGE_MS) return null;
  return rec.id;
}

// The one-time connect link a viewer opens/scans: origin + path + #<hostId>.
// The hash is the only thing that ever varies, so build it in one place.
export function hostLink(id) {
  return `${window.location.origin}${window.location.pathname}#${id}`;
}

// Render our connect/invite QR into `el` with the app's fixed styling. Both the
// host's connect card and the parent's invite overlay share these options.
export function renderQR(el, text) {
  if (!el) return;
  el.replaceChildren();
  QrCreator.render(
    { text, radius: 0.1, ecLevel: 'H', fill: '#000', background: '#fff', size: 400 },
    el
  );
}

// Copy `text` and briefly flash `labelEl` to "Copied!". Returns whether the
// write succeeded so the caller can decide whether to log analytics.
export async function copyWithFeedback(text, labelEl) {
  try {
    await navigator.clipboard.writeText(text);
    if (labelEl) {
      const prev = labelEl.textContent;
      labelEl.textContent = t('common.copied');
      setTimeout(() => { labelEl.textContent = prev; }, 1600);
    }
    return true;
  } catch (e) {
    console.warn('[CAMMIRROR] clipboard write failed', e && e.name);
    return false;
  }
}

// Reflect a boolean toggle state onto a button's aria-pressed attribute.
export function setPressed(el, on) {
  if (el) el.setAttribute('aria-pressed', String(!!on));
}

// --- Toasts -----------------------------------------------------------------
// A toast is a `hidden` element that fades in/out via a `.show` class (see
// styles.css). Both the parent's transient messages and the "update available"
// prompt use this same reveal/dismiss dance, so it lives here once.

// Must match the toast fade duration in styles.css so we only set `hidden`
// after the fade-out has finished.
const TOAST_FADE_MS = 220;

// Reveal a toast with its fade-in transition. Reading offsetWidth forces the
// browser to apply `hidden = false` before the `.show` class, so the transition
// actually animates instead of snapping — this also lets a fresh message
// re-trigger the animation when it replaces a visible toast.
export function openToast(el) {
  if (!el) return;
  el.hidden = false;
  void el.offsetWidth; // force reflow so the .show transition runs
  el.classList.add('show');
}

// Fade a toast out, then hide it once the transition finishes — but only if it
// wasn't re-shown meanwhile. `onHidden` runs when it's actually hidden, letting
// callers reset their own "is a toast visible?" state.
export function closeToast(el, onHidden) {
  if (!el) return;
  el.classList.remove('show');
  setTimeout(() => {
    if (el.classList.contains('show')) return;
    el.hidden = true;
    if (onHidden) onHidden();
  }, TOAST_FADE_MS);
}

// Rough iOS/iPadOS detection. iPadOS 13+ reports as desktop macOS, so we also
// treat a Mac-like UA with a touchscreen as iOS. Callers layer any extra checks
// (e.g. "is this Safari?") on top of this.
export function isIos() {
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  const macLike = /Macintosh/.test(ua) || navigator.platform === 'MacIntel';
  return macLike && navigator.maxTouchPoints > 1;
}

// Keep the screen from sleeping while the app is active. A baby-monitor is
// useless if the phone dims and suspends the media stream. The OS auto-releases
// the lock whenever the tab is hidden, so we re-acquire it every time the page
// becomes visible again.
// Render a message inside the connect panel (where the QR / "Scan to connect"
// prompt lives) instead of a native alert() or console-only log. On a phone
// acting as the host, a blocking alert() is easy to miss and feels broken; an
// in-page message matches how every other failure is surfaced. textContent is
// used so a message can never inject markup.
export function showConnectError(message, options = {}) {
  console.error('[CAMMIRROR]', message);
  const $connect = document.getElementById('connect');
  if (!$connect) return;
  $connect.classList.remove('connected');
  $connect.classList.add('is-error');

  // Hide (don't destroy) the existing card content — including the already
  // rendered QR and its wired buttons — so a Retry that succeeds can restore it
  // intact via clearConnectError() instead of rebuilding markup + listeners.
  Array.from($connect.children).forEach((el) => {
    if (el.classList.contains('connect-error-box')) return;
    el.dataset.hiddenByError = '1';
    el.hidden = true;
  });

  const existing = $connect.querySelector('.connect-error-box');
  if (existing) existing.remove();
  const $box = document.createElement('div');
  $box.className = 'connect-error-box';

  const $msg = document.createElement('p');
  $msg.className = 'connect-error-msg';
  $msg.textContent = message;
  $box.appendChild($msg);

  // Optional in-place Retry: re-run the caller's recovery (e.g. re-request the
  // camera) without a full reload, so a resolved hiccup restores the monitor.
  if (options.retryLabel && typeof options.onRetry === 'function') {
    const $retry = document.createElement('button');
    $retry.type = 'button';
    $retry.className = 'btn';
    $retry.textContent = options.retryLabel;
    $retry.addEventListener('click', () => options.onRetry());
    $box.appendChild($retry);
  }

  // A dead-end error needs a way out: offer Back to the role picker. Reloading at
  // the bare path tears down every peer/camera/timer cleanly (mirrors resetToRole).
  if (options.backLabel) {
    const $back = document.createElement('button');
    $back.type = 'button';
    $back.className = 'btn subtle reset-btn';
    $back.textContent = options.backLabel;
    $back.addEventListener('click', () => window.location.assign(window.location.pathname));
    $box.appendChild($back);
  }

  $connect.appendChild($box);
  // flex (not block) keeps the card's centered column layout + even padding.
  $connect.style.display = 'flex';
}

// Undo showConnectError: drop the error box and reveal the original card
// content (QR + buttons) that was hidden rather than destroyed. Safe to call
// when no error is showing.
export function clearConnectError() {
  const $connect = document.getElementById('connect');
  if (!$connect) return;
  const $box = $connect.querySelector('.connect-error-box');
  if ($box) $box.remove();
  $connect.classList.remove('is-error');
  Array.from($connect.children).forEach((el) => {
    if (el.dataset && el.dataset.hiddenByError) {
      delete el.dataset.hiddenByError;
      el.hidden = false;
    }
  });
}

// Map a getUserMedia failure to a short, actionable message key. The browser's
// raw err.message ("Could not start video source", etc.) is meaningless to
// parents, so we translate the DOMException name into human guidance instead.
export function cameraErrorKey(err) {
  switch (err && err.name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'error.camera.denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'error.camera.notFound';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'error.camera.busy';
    default:
      return 'error.camera.generic';
  }
}

let wakeLock = null;
export function keepScreenAwake() {
  if (!('wakeLock' in navigator)) return;

  const acquire = async () => {
    try {
      wakeLock = await navigator.wakeLock.request('screen');
      wakeLock.addEventListener('release', () => { wakeLock = null; });
    } catch (err) {
      console.warn('[CAMMIRROR] Screen wake lock unavailable:', err && err.name);
    }
  };

  acquire();
  document.addEventListener('visibilitychange', () => {
    if (wakeLock === null && document.visibilityState === 'visible') acquire();
  });
}

export function playVideo($video) {
  const playPromise = $video.play();
  if (playPromise && playPromise.catch) {
    playPromise.catch((error) => console.log("[ERROR] Couldn't play..", error));
  }
}

export function setConstraints(cameras) {
    var constraints = [];
    cameras.forEach((camera) => {
      let isActive = activeVideoStream.getVideoTracks()[0].getSettings().deviceId === camera.deviceId;
      constraints.push({
        video: { 
          deviceId: { exact: camera.deviceId },
          isActive
        }
      });
    });
    return constraints;
}

// Advance the round-robin camera selection and return the getUserMedia
// constraints for the next device, or null when there's nothing to switch to
// (fewer than two cameras). This only owns device *selection* — it updates the
// active flag in `constraints` and hands back constraints; capturing the stream
// and swapping it into the live calls is the host's job (see Host.switchCamera),
// which keeps camera switching on the same recovery path as everything else
// instead of duplicating it here.
export function nextCameraConstraints() {
    if (!constraints || constraints.length <= 1) return null;
    // Rotate to the device after the active one (wrapping around) so phones that
    // expose three or more cameras cycle through all of them, instead of just
    // ping-ponging between the first two. A missing active flag (-1) wraps to 0.
    const activeIndex = constraints.findIndex((c) => c.video.isActive);
    const nextCameraIndex = (activeIndex + 1) % constraints.length;
    constraints.forEach((constraint) => { constraint.video.isActive = false; });
    constraints[nextCameraIndex].video.isActive = true;
    return {
        video: {
            deviceId: constraints[nextCameraIndex].video.deviceId,
            frameRate: { ideal: 15, max: 20 },
        },
        audio: { echoCancellation: true },
    };
}

// A single shared AudioContext is reused across streams. Creating a new one
// per call (the previous behaviour) leaked contexts until the browser refused
// to create more (~6), silently breaking the meter. The meter is driven by
// requestAnimationFrame (throttled to ~90ms) so it pauses in background tabs.
let sharedAudioContext = null;
let currentAnalyserSource = null;
let volumeRafId = null;

// `onLevel(level)` — optional callback invoked each tick with a normalised
// 0..1 loudness. The parent uses it to drive the "time since sound" indicator
// off this single analyser instead of opening a second AudioContext.
export function showAudioLevels(stream, onLevel) {
  const $levels = document.getElementById('volume-levels');
  const spans = $levels ? Array.from($levels.querySelectorAll('.group span')) : [];

  if (!sharedAudioContext) {
    sharedAudioContext = new AudioContext();
  }
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume();
  }

  if (currentAnalyserSource) {
    try { currentAnalyserSource.disconnect(); } catch (e) { /* already disconnected */ }
  }

  const audioSource = sharedAudioContext.createMediaStreamSource(stream);
  const analyser = sharedAudioContext.createAnalyser();

  analyser.fftSize = 512;
  analyser.minDecibels = -127;
  analyser.maxDecibels = 0;
  analyser.smoothingTimeConstant = 0.4;

  audioSource.connect(analyser);
  currentAnalyserSource = audioSource;

  const volumes = new Uint8Array(analyser.frequencyBinCount);
  let count = 0;
  let lastTick = 0;

  if (volumeRafId) cancelAnimationFrame(volumeRafId);

  const render = (now) => {
    volumeRafId = requestAnimationFrame(render);
    if (now - lastTick < 90) return;
    lastTick = now;

    analyser.getByteFrequencyData(volumes);
    let volumeSum = 0;
    for (const volume of volumes) volumeSum += volume;
    const averageVolume = volumeSum / volumes.length;

    if (spans.length) {
      const volume = averageVolume * 100 / 312; // / 127;
      spans[count].style.height = volume + 'px';
      count = (count + 1) % spans.length;
    }

    if (onLevel) onLevel(Math.min(1, averageVolume / 90));
  };

  volumeRafId = requestAnimationFrame(render);
}
